import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { Modality } from '~/domain/dicom/modality';
import {
    isThresholdSupportedModality,
    modalityDefaultRange,
    resolvePreset,
    ThresholdPreset,
    thresholdPresets,
} from '~/engine/tool/actions/contouring-actions/intensityThreshold';
import { intensityThresholdConstraintState } from '~/state/atoms/toolsAtoms';

/** A PET SUV factor is usable only when finite and positive (guards NaN). */
function isSuvFactorUsable(suvFactor: number | null): boolean {
    return suvFactor !== null && Number.isFinite(suvFactor) && suvFactor > 0;
}

/**
 * Convert a raw image voxel value to the modality's display unit: HU passes
 * through for CT; PET raw activity is scaled to SUVbw by the image's suvFactor
 * (only when the factor is usable — otherwise raw is returned and the caller
 * must gate on {@link isSuvFactorUsable} so raw activity is never shown as SUV).
 */
function rawToDisplay(
    raw: number,
    modality: Modality | undefined,
    suvFactor: number | null
): number {
    if (modality === Modality.PT && isSuvFactorUsable(suvFactor)) {
        return raw * (suvFactor as number);
    }
    return raw;
}

/** View-model exposed by {@link useIntensityThreshold} to the section UI. */
export interface IntensityThresholdViewModel {
    /**
     * Threshold can apply to the active image: supported modality, usable SUV
     * (if PET), and a non-degenerate intensity range to threshold over.
     */
    available: boolean;
    /** Whether the constraint is enabled in state. */
    enabled: boolean;
    /** i18n id for the label tooltip (reason when unavailable, help otherwise). */
    labelTooltipId: string;
    /** i18n id for the unavailable-reason tooltip (used by the warning icon). */
    unavailableTooltipId: string;
    /** True when the slider bounds are a non-degenerate range. */
    hasRange: boolean;
    /** Slider bounds + current handles, in display units. */
    displayMin: number;
    displayMax: number;
    sliderValue: [number, number];
    sliderStep: number;
    /** Short unit label (HU / SUV) shown next to the values. */
    unitLabel: string;
    /** Format a display value for the active modality (SUV 1 dp, HU integer). */
    formatValue: (value: number) => string;
    /** Modality-specific quick-select preset bands. */
    presets: readonly ThresholdPreset[];
    /** Index of the preset whose range matches the current selection, or null. */
    activePresetIndex: number | null;
    onToggle: (enabled: boolean) => void;
    onRangeChange: (value: number | number[]) => void;
    onApplyPreset: (presetIndex: number) => void;
}

/**
 * Encapsulates the intensity-threshold modifier's state, unit conversion,
 * default seeding, and handlers, exposing a flat view-model for the section UI.
 *
 * Called from the always-mounted control (not the popover body) so the default
 * range is seeded when first enabled even while the popover is closed.
 */
export function useIntensityThreshold(): IntensityThresholdViewModel {
    const image = useActiveEditTarget().environment?.image?.parent;
    const [threshold, setThreshold] = useRecoilState(
        intensityThresholdConstraintState
    );

    const modality = image?.firstSlice.modality;
    const suvFactor = image?.suvFactor ?? null;
    const supported = isThresholdSupportedModality(modality);
    // PET needs a usable (finite, positive) SUV factor; a malformed series can
    // yield NaN, which must not read as "SUV present" (else the slider would
    // show raw activity labelled SUV).
    const suvUsable = modality !== Modality.PT || isSuvFactorUsable(suvFactor);

    // Slider bounds, in display units (HU for CT, SUVbw for PET). Computed from
    // modality/SUV only — NOT from `available` — so `hasRange` below can feed
    // into `available` without a circular dependency.
    const { displayMin, displayMax } = useMemo(() => {
        if (!image || !supported || !suvUsable) {
            return { displayMin: 0, displayMax: 0 };
        }
        const a = rawToDisplay(image.minValue, modality, suvFactor);
        const b = rawToDisplay(image.maxValue, modality, suvFactor);
        return { displayMin: Math.min(a, b), displayMax: Math.max(a, b) };
    }, [image, supported, suvUsable, modality, suvFactor]);

    const hasRange = displayMax > displayMin;
    // The modifier can apply only to a supported image with usable SUV (if PET)
    // AND a non-degenerate range; a uniform image has nothing to threshold over.
    const available = !!image && supported && suvUsable && hasRange;

    const sliderStep = modality === Modality.PT ? 0.1 : 1;
    const unitLabel = modality === Modality.PT ? 'SUV' : 'HU';
    const presets = useMemo(() => thresholdPresets(modality), [modality]);

    const formatValue = useCallback(
        (value: number) =>
            modality === Modality.PT ? value.toFixed(1) : value.toFixed(0),
        [modality]
    );

    // Seed the range from the modality default exactly ONCE — the first time
    // Threshold is enabled for a usable image. The `seeded` flag lives in the
    // atom (not a component-local ref), so toggling off/on, switching image, or
    // the control unmounting / remounting never re-seeds and clobbers a
    // deliberate band. Manual range / preset changes also mark it seeded.
    useEffect(() => {
        if (!threshold.enabled || !available || threshold.seeded) {
            return;
        }
        const def = modalityDefaultRange(modality, displayMin, displayMax);
        setThreshold((c) => ({
            ...c,
            lower: def.lower,
            upper: def.upper,
            seeded: true,
        }));
    }, [
        threshold.enabled,
        threshold.seeded,
        available,
        modality,
        displayMin,
        displayMax,
        setThreshold,
    ]);

    const onToggle = useCallback(
        (enabled: boolean) => setThreshold((c) => ({ ...c, enabled })),
        [setThreshold]
    );
    const onRangeChange = useCallback(
        (value: number | number[]) => {
            if (!Array.isArray(value)) {
                return;
            }
            const [lower, upper] = value;
            setThreshold((c) => ({ ...c, lower, upper, seeded: true }));
        },
        [setThreshold]
    );
    const onApplyPreset = useCallback(
        (presetIndex: number) => {
            const preset = presets[presetIndex];
            if (!preset) {
                return;
            }
            const range = resolvePreset(preset, displayMin, displayMax);
            setThreshold((c) => ({
                ...c,
                lower: range.lower,
                upper: range.upper,
                seeded: true,
            }));
        },
        [presets, displayMin, displayMax, setThreshold]
    );

    // Clamp the displayed handles to the image's range so they stay valid even
    // if the stored range predates the current image.
    const sliderValue: [number, number] = [
        Math.min(Math.max(threshold.lower, displayMin), displayMax),
        Math.min(Math.max(threshold.upper, displayMin), displayMax),
    ];

    // Highlight a preset chip when the current selection matches its resolved
    // range (within half a slider step, to absorb float rounding — presets are
    // separated by far more than that).
    const presetTolerance = sliderStep / 2;
    const matchedPresetIndex = available
        ? presets.findIndex((preset) => {
              const range = resolvePreset(preset, displayMin, displayMax);
              return (
                  Math.abs(range.lower - sliderValue[0]) <= presetTolerance &&
                  Math.abs(range.upper - sliderValue[1]) <= presetTolerance
              );
          })
        : -1;

    const unavailableTooltipId = !supported
        ? 'thresholdUnavailableModality'
        : !suvUsable
          ? 'thresholdUnavailableSuv'
          : 'thresholdUnavailableRange';

    return {
        available,
        enabled: threshold.enabled,
        labelTooltipId: available
            ? 'thresholdConstraintTooltip'
            : unavailableTooltipId,
        unavailableTooltipId,
        hasRange,
        displayMin,
        displayMax,
        sliderValue,
        sliderStep,
        unitLabel,
        formatValue,
        presets,
        activePresetIndex: matchedPresetIndex >= 0 ? matchedPresetIndex : null,
        onToggle,
        onRangeChange,
        onApplyPreset,
    };
}
